import React from 'react';
import i18next from 'i18next';
import { MenuItem, Stack, TextField, type SxProps, type Theme } from '@mui/material';
import type {
    IColorRGB,
    IDropdownOption,
    ILineEndings,
    ILineStyle,
    ILineWidth,
} from '../../../../types';
import { ReduxUpdater } from '../../../../pdf/ReduxUpdater';
import { BorderInfoToStyleString, isButtonPresent } from '../../../../pdf/stateHandler/Helper';
import {
    borderStylesToDropdownOptions,
    canEditAnyAnnot,
    colorsToDropdownOptions,
    concatClassNames,
    convertOpacity,
    dateTimeFormat,
    devWarning,
    findCommonProperties,
    genRandomKey,
    intColor,
    lineEndingsToDropdownOptions,
    lineWidthsToDropdownOptions,
    removeTransparency,
    toCSSColor,
    translateLineStyle,
    transparencyPercentToOpacity,
    upperCaseBegin,
} from '../../../../utils';
import Icon from '@mdi/react';
import { mdiFormatColorFill, mdiPalette } from '@mdi/js';

import styles from './AnnotationProperties.module.scss';
import {
    lineEndingEndIcon,
    lineEndingStartIcon,
    lineStyleIcon,
    lineWidthIcon,
} from '../../Ribbon/Ribbon';
import { useSelector } from 'react-redux';
import SliderProperty from './Properties/Slider';
import { GlobalVariables } from '../../../../pdf/GlobalVariables';
import {
    eplassSliceAnnotationSidepanelRefresh,
    eplassSliceViewMode,
} from '../../../../Redux/EplassSlice';
import { Opacity } from '@mui/icons-material';
import type { Core } from '@pdftron/webviewer';
import { ApryseSDK, type MeasurementAnnotation } from '../../../../pdf/ApryseSDK';
import { apryseEventSliceSelectedAnnotationIds } from '../../../../Redux/ApryseEventSlice';
import { ApryseUpdater } from '../../../../pdf/ApryseUpdater';
import { PdfViewerViewModeFlag } from '../../../../pdf/Enums';
import { CustomHistoryManager } from '../../../../pdf/CustomHistoryManager';
import { useDarkMode } from '../../../../DarkModeContext';

/**
 * Component that shows a configurable menu structure in a Microsoft-Office-like structure.
 */
export default () => {
    const { isDark } = useDarkMode();
    const selectedAnnotationsIds = useSelector(apryseEventSliceSelectedAnnotationIds);
    const forceRefresh = useSelector(eplassSliceAnnotationSidepanelRefresh);

    const [displayDegrees, setDisplayDegrees] = React.useState<number>(0);
    const [currentTransparency, setCurrentTransparency] = React.useState(0);
    const currentViewMode = useSelector(eplassSliceViewMode);

    /**
     * sx-Prop für MUI TextField im Dark Mode:
     * disabled  → stark gedimmt, kaum Rahmen  (wie Light Mode)
     * enabled   → heller Text, sichtbarer Rahmen
     */

    const darkTextFieldSx = (disabled: boolean): SxProps<Theme> => {
        if (!isDark) {
            return getLightModeTextFieldSx(disabled);
        }
        return getDarkModeTextFieldSx(disabled);
    };
    const getLightModeTextFieldSx = (disabled: boolean): SxProps<Theme> => ({
        // MUI setzt opacity:0.38 auf dem OutlinedInput-Root — hier auf FormControl-Ebene
        // die Kinder explizit auf volle Sichtbarkeit zwingen
        '& .MuiOutlinedInput-root': {
            opacity: '1 !important' as any,
        },
        '& .MuiInputBase-input': {
            WebkitTextFillColor: disabled ? '#222' : 'inherit',
            opacity: '1 !important' as any,
        },
        '& .MuiInputLabel-root': {
            opacity: '1 !important' as any,
            color: disabled ? '#222' : undefined,
        },
        '& .MuiSelect-icon': {
            opacity: '1 !important' as any,
            color: disabled ? '#444' : undefined,
        },
        '& .MuiOutlinedInput-notchedOutline': {
            opacity: '1 !important' as any,
            borderColor: disabled ? 'rgba(0,0,0,0.3)' : undefined,
        },
    });
    const getDarkModeTextFieldSx = (disabled: boolean): SxProps<Theme> => ({
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: disabled ? 'rgba(255,255,255,0.15)' : '#777',
        },
        '& .MuiOutlinedInput-root:not(.Mui-disabled):hover': {
            backgroundColor: disabled ? undefined : '#2a3a40',
            borderRadius: disabled ? undefined : '7px',
        },
        '& .MuiOutlinedInput-root:not(.Mui-disabled):hover .MuiOutlinedInput-notchedOutline': {
            borderColor: disabled ? 'rgba(255,255,255,0.15)' : '#88D2E7',
            borderRadius: disabled ? undefined : '7px',
        },
        '& .MuiInputBase-input': {
            color: disabled ? 'rgba(224,224,224,0.35)' : '#e0e0e0',
            WebkitTextFillColor: disabled ? 'rgba(224,224,224,0.35)' : '#e0e0e0',
        },
        '& .MuiSelect-icon': {
            color: disabled ? 'rgba(224,224,224,0.25)' : '#aaa',
            opacity: disabled ? 0.3 : 1,
        },
        '& .MuiInputLabel-root': {
            color: disabled ? 'rgba(224,224,224,0.35)' : '#aaa',
        },
    });

    const isAnnotationEditMode = (): boolean =>
        currentViewMode === PdfViewerViewModeFlag.Annotation;

    const getCurrentTransparency = (markup: Core.Annotations.MarkupAnnotation) =>
        Math.round(convertOpacity(markup.Opacity));

    const selectedAnnots = React.useMemo(() => {
        if (selectedAnnotationsIds.length === 1) {
            const selectedAnnotation = ApryseUpdater.selectedAnnotations[0];
            setDisplayDegrees(ApryseSDK.normaliseDegrees(-selectedAnnotation.Rotation));

            if (ApryseSDK.isMarkup(selectedAnnotation)) {
                setCurrentTransparency(
                    getCurrentTransparency(selectedAnnotation as Core.Annotations.MarkupAnnotation),
                );
            }
        }
        return ApryseUpdater.selectedAnnotations;
    }, [selectedAnnotationsIds]);

    const renderStylingProps = (annots: Core.Annotations.Annotation[]) => {
        const commonProps: string[] = findCommonProperties(annots);

        if (!commonProps) {
            return null;
        }

        const renderPropByName = (annots: Core.Annotations.Annotation[], propName: string) => {
            switch (propName) {
                case 'borderColor':
                    return renderMultiSelectionBorderColor(annots);
                case 'borderStyle':
                    return renderMultiSelectionBorderStyle(annots);
                case 'borderWidth':
                    return renderMultiSelectionBorderWidth(annots);
                case 'color':
                    return renderMultiSelectionColor(annots);
                case 'fillColor':
                    return renderMultiSelectionFillColor(annots);
                case 'lineColor':
                    return renderMultiSelectionLineColor(annots);
                case 'lineEnd':
                    return renderMultiSelectionLineEnding(annots);
                case 'lineStart':
                    return renderMultiSelectionLineStart(annots);
                case 'lineStyle':
                    return renderMultiSelectionLineStyle(annots);
                case 'lineCloudyStyle':
                    return renderMultiSelectionCloudyLineStyle(annots);
                case 'lineWidth':
                    return renderMultiSelectionLineWidth(annots);
                case 'opacity':
                    return renderMultiSelectionTransparency(annots);
                case 'ratio':
                    return renderMultiSelectionRatio(annots);
                case 'rotation':
                    return renderMultiSelectionRotation(annots);

                default:
                    devWarning(
                        `Unexpected annotation property '${propName}': there is no corresponding handling implemented yet.`,
                    );
                    return null;
            }
        };

        return (
            <>
                <h3 style={{ margin: '0px', padding: '8px 0px 0px 0px' }}>
                    {i18next.t('annotPropsPanel.appearance')}
                </h3>
                <br />
                {commonProps.map(prop => renderPropByName(annots, prop))}
            </>
        );
    };

    const renderMultiSelectionRatio = React.useCallback(
        (annots: Core.Annotations.Annotation[]) => {
            const annot = annots[0];

            const { pageScale, worldScale } = ApryseSDK.getMeasureRatio(annot as MeasurementAnnotation);

            /*
            let userUnit: string | null = first.userSpaceUnit;
            let realUnit: string | null = first.realUnit;

            let annotType = ApryseSDK.annotPrettyType(annots[0]);

            // For multiselect
            if (annots.length > 1) {
                // If not all annots are same type >> set empty
                if (
                    !annots
                        .slice(1)
                        .every(annot => ApryseSDK.annotPrettyType(annot) === annotType)
                ) {
                    annotType = '';
                }

                // If not all 'userSpaceValue's equal >> set empty
                if (
                    ratioUserValue !== '' &&
                    !annots
                        .slice(1)
                        .every(
                            current =>
                                getMeasureRatio(current).userSpaceValue === first.userSpaceValue,
                        )
                ) {
                    setRatioUserValue('');
                }

                // If not all 'userSpaceUnit's equal >> set null
                if (
                    !annots
                        .slice(1)
                        .every(
                            current =>
                                getMeasureRatio(current).userSpaceUnit === first.userSpaceUnit,
                        )
                ) {
                    userUnit = null;
                }

                // If not all 'realValue's equal >> set empty
                if (
                    ratioRealValue !== '' &&
                    !annots
                        .slice(1)
                        .every(current => getMeasureRatio(current).realValue === first.realValue)
                ) {
                    setRatioRealValue('');
                }

                // If not all 'realUnit's equal >> set null
                if (
                    !annots
                        .slice(1)
                        .every(current => getMeasureRatio(current).realUnit === first.realUnit)
                ) {
                    realUnit = null;
                }
            }

            const userUnitOptions = ReduxUpdater.lists.userUnits ?? [];
            const realUnitOptions = ReduxUpdater.lists.realUnits ?? [];

            const updateMeasurementInfoDialog = (
                measurement: Core.Annotations.Annotation,
                newRatio: PDFViewCtrl.PDF.MeasureRatio,
            ) => {
                const value = calculateMeasurementValue(measurement, newRatio);

                if (GlobalDialogs.measurementInfo !== null) {
                    GlobalDialogs.showMeasureDialog({
                        ...GlobalDialogs.measurementInfo,
                        ratio: newRatio,
                        value: value,
                    });
                }
            };

            const onUserValueChanged = (newUserValue: string) => {
                setRatioUserValue(newUserValue);
            };

            const onRealValueChanged = (newRealValue: string) => {
                setRatioRealValue(newRealValue);
            };

            const onUserValueConfirmed = () => {
                if (ratioUserValue) {
                    const newNumber = Number(ratioUserValue);

                    if (newNumber !== 0) {
                        Promise.all(
                            annots.map(annot => {
                                const newRatio: PDFViewCtrl.PDF.MeasureRatio = {
                                    ...getMeasureRatio(annot),
                                    userSpaceValue: newNumber,
                                };
                                return setMeasureRatio(annot, newRatio).then(() => {
                                    updateMeasurementCaption(annot, newRatio);
                                });
                            }),
                        ).then(() => {
                            const lastAnnot = annots[annots.length - 1];
                            const lastRatio = {
                                ...getMeasureRatio(lastAnnot),
                                userSpaceValue: newNumber,
                            };
                            updateMeasurementInfoDialog(lastAnnot, lastRatio);
                            setRatioUserValue(newNumber.toString());
                        });
                    }
                }
            };

            const onRealValueConfirmed = () => {
                if (ratioRealValue) {
                    const newNumber = Number(ratioRealValue);

                    if (newNumber !== 0) {
                        Promise.all(
                            annots.map(annot => {
                                const newRatio: PDFViewCtrl.PDF.MeasureRatio = {
                                    ...getMeasureRatio(annot),
                                    realValue: newNumber,
                                };
                                return setMeasureRatio(annot, newRatio).then(() => {
                                    updateMeasurementCaption(annot, newRatio);
                                });
                            }),
                        ).then(() => {
                            const lastAnnot = annots[annots.length - 1];
                            const lastRatio = {
                                ...getMeasureRatio(lastAnnot),
                                realValue: newNumber,
                            };
                            updateMeasurementInfoDialog(lastAnnot, lastRatio);
                            setRatioRealValue(newNumber.toString());
                        });
                    }
                }
            };

            const updateUserUnitFn = (newUnit: PDFViewCtrl.PDF.annots.constant.Annot_Unit_Type) => {
                Promise.all(
                    annots.map(annot => {
                        const newRatio: PDFViewCtrl.PDF.MeasureRatio = {
                            ...getMeasureRatio(annot),
                            userSpaceUnit: newUnit,
                        };
                        return setMeasureRatio(annot, newRatio).then(() => {
                            updateMeasurementCaption(annot, newRatio);
                        });
                    }),
                ).then(() => {
                    const lastAnnot = annots[annots.length - 1];
                    const lastRatio = { ...getMeasureRatio(lastAnnot), userSpaceUnit: newUnit };
                    updateMeasurementInfoDialog(lastAnnot, lastRatio);
                });
            };

            const updateRealUnitFn = (newUnit: string) => {
                Promise.all(
                    annots.map(annot => {
                        const newRatio: PDFViewCtrl.PDF.MeasureRatio = {
                            ...getMeasureRatio(annot),
                            realUnit: newUnit,
                        };
                        return setMeasureRatio(annot, newRatio).then(() => {
                            updateMeasurementCaption(annot, newRatio);
                        });
                    }),
                ).then(() => {
                    const lastAnnot = annots[annots.length - 1];
                    const lastRatio = { ...getMeasureRatio(lastAnnot), realUnit: newUnit };
                    updateMeasurementInfoDialog(lastAnnot, lastRatio);
                });
            };
        */

            return (
                <>
                    {renderRatioProperty(
                        'distance',
                        i18next.t('annotPropsPanel.specificProps.scale'),
                        pageScale.value.toString(),
                        pageScale.unit,
                        false,
                    )}
                    {renderRatioProperty(
                        'distance',
                        '=',
                        worldScale.value.toString(),
                        worldScale.unit,
                        false,
                    )}
                </>
            );
        },
        [currentViewMode, selectedAnnotationsIds /*, ratioUserValue, ratioRealValue*/],
    );

    const renderMultiSelectionRotation = React.useCallback(
        (annots: Core.Annotations.Annotation[]) => {
            const showEditMode = isAnnotationEditMode() && canEditAnyAnnot(annots);

            let allEqual: boolean;
            const firstValue = annots[0].Rotation;

            if (annots.length === 1) {
                allEqual = true;
            } else {
                allEqual = annots.slice(1).every(annot => annot.Rotation === firstValue);
            }

            const title = i18next.t('annotPropsPanel.appearanceProps.rotation');
            const minCCW = -180;
            const maxCW = 180;
            const rotationSteps = 10;
            const formatValueFn = (degrees: number) => (allEqual ? `${degrees}°` : '');
            const updateValueFn = (newDegrees: number) => {
                if (newDegrees < minCCW || newDegrees > maxCW) {
                    return;
                }
                if (newDegrees === displayDegrees) {
                    return;
                }

                annots.forEach(annot => ApryseSDK.annotRotation(annot, newDegrees));
                setDisplayDegrees(newDegrees);
                ReduxUpdater.annotationSidepanelRefresh();
            };

            return (
                <SliderProperty
                    icon={<Opacity />}
                    title={title}
                    isEditMode={showEditMode}
                    initialValue={displayDegrees}
                    minValue={minCCW}
                    maxValue={maxCW}
                    scrollSteps={rotationSteps}
                    updateValueFn={updateValueFn}
                    formatValueFn={formatValueFn}
                />
            );
        },
        [displayDegrees],
    );

    const renderMultiSelectionTransparency = React.useCallback(
        (annots: Core.Annotations.Annotation[]) => {
            const showEditMode = isAnnotationEditMode() && canEditAnyAnnot(annots);

            let allEqual: boolean;
            const firstValue = ApryseSDK.annotOpacity(annots[0]);

            if (annots.length === 1) {
                allEqual = true;
            } else {
                allEqual = annots
                    .slice(1)
                    .every(annot => ApryseSDK.annotOpacity(annot) === firstValue);
            }

            const title = i18next.t('annotPropsPanel.appearanceProps.transparency');
            const getValueFn = () => currentTransparency;
            const minTransparency = 0;
            const maxTransparency = 100;
            const transparencySteps = 5; // used for mousewheel
            const formatValueFn = (value: number) => (allEqual ? `${Math.round(value)}%` : '');
            const readyForNext = true;

            const updateValueFn = (newPercent: number) => {
                if (!readyForNext) {
                    return;
                }
                if (newPercent < minTransparency || newPercent > maxTransparency) {
                    return;
                }
                if (newPercent === getValueFn()) {
                    return;
                }

                const newOpacity = transparencyPercentToOpacity(newPercent);
                annots.forEach(annot => ApryseSDK.annotOpacity(annot, newOpacity));
                setCurrentTransparency(newPercent);
                ReduxUpdater.annotationSidepanelRefresh();
                ReduxUpdater.ribbonRefresh();
            };

            return (
                <SliderProperty
                    icon={<Opacity />}
                    title={title}
                    isEditMode={showEditMode}
                    initialValue={currentTransparency}
                    minValue={minTransparency}
                    maxValue={maxTransparency}
                    scrollSteps={transparencySteps}
                    updateValueFn={updateValueFn}
                    formatValueFn={formatValueFn}
                />
            );
        },
        [currentTransparency, currentViewMode],
    );

    const renderRatioProperty = (
        annotType: string,
        title: string | null,
        value: string,
        unit: string | null,
        // unitOptions: string[],
        showEditMode: boolean,
        //onValueChanged: (newValue: string) => void,
        //onValueConfirmed: () => void,
        //updateUnitFn: (newUnit: PDFViewCtrl.PDF.annots.constant.Annot_Unit_Type) => void,
    ) => {
        /*
        function onRatioUnitChanged(ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
            if (ev.target.value) {
                const newRatioUnit =
                    PDFViewCtrl.PDF.annots.constant.Annot_Unit_Type[ev.target.value];
                updateUnitFn(newRatioUnit);
            }
        }

        function onRatioValueChanged(
            ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        ) {
            onValueChanged(ev.target.value);
        }

        function onRatioValueKeyDown(ev: React.KeyboardEvent<HTMLDivElement>) {
            if (ev.key === 'Enter') {
                onValueConfirmed();
            }

            if (
                ![
                    'Backspace',
                    'Delete',
                    'ArrowLeft',
                    'ArrowRight',
                    '0',
                    '1',
                    '2',
                    '3',
                    '4',
                    '5',
                    '6',
                    '7',
                    '8',
                    '9',
                    '.',
                ].includes(ev.key)
            ) {
                ev.preventDefault();
            }
        }
        */

        return (
            <Stack direction="row">
                <TextField
                    //className={styles.dropDown}
                    fullWidth
                    // disabled={!showEditMode}
                    InputProps={{ readOnly: true }}
                    label={title}
                    value={value}
                    //onChange={onRatioValueChanged}
                    //onKeyDown={onRatioValueKeyDown}
                    //onBlur={onValueConfirmed}
                    margin="dense"
                    size="small"
                />
                <TextField
                    //className={styles.dropDown}
                    fullWidth
                    //select
                    // disabled={!showEditMode}
                    InputProps={{ readOnly: true }}
                    value={unit}
                    //onChange={onRatioUnitChanged}
                    margin="dense"
                    size="small"
                    style={{ marginLeft: '10px' }}
                ></TextField>
            </Stack>
        );

        /* 
        {unitOptions.map(unitOption => (
            <MenuItem className={styles.dropDown} key={unitOption} value={unitOption}>
                {i18next.t(`measurements.units.${unitOption}`)}
            </MenuItem>
        ))}
        */
    };

    const renderMultiSelectionLineStart = (annots: Core.Annotations.Annotation[]) => {
        const showEditMode = isAnnotationEditMode() && canEditAnyAnnot(annots);

        let allEqual: boolean;
        const firstValue = ApryseSDK.annotStartStyle(annots[0]);

        if (annots.length === 1) {
            allEqual = true;
        } else {
            allEqual = annots
                .slice(1)
                .every(annot => ApryseSDK.annotStartStyle(annot) === firstValue);
        }

        const setNewLineStart = (newLineStart: Core.Annotations.LineEndType) => {
            const snapshotBefore = CustomHistoryManager.takeAnnotationsSnapshot();
            annots.forEach(annot => ApryseSDK.annotStartStyle(annot, newLineStart));

            ApryseSDK.handleAnnotationChanged(annots, 'modify', 'lineStart', snapshotBefore);
            ReduxUpdater.annotationSidepanelRefresh();
            ReduxUpdater.ribbonRefresh();
        };

        const initialValue = allEqual ? firstValue : undefined;

        return renderLineStart(initialValue, showEditMode, setNewLineStart);
    };

    const renderMultiSelectionLineEnding = (annots: Core.Annotations.Annotation[]) => {
        const showEditMode = isAnnotationEditMode() && canEditAnyAnnot(annots);

        let allEqual: boolean;
        const firstValue = ApryseSDK.annotEndStyle(annots[0]);

        if (annots.length === 1) {
            allEqual = true;
        } else {
            allEqual = annots
                .slice(1)
                .every(annot => ApryseSDK.annotEndStyle(annot) === firstValue);
        }

        const setNewLineEnding = (newLineEnding: Core.Annotations.LineEndType) => {
            const snapshotBefore = CustomHistoryManager.takeAnnotationsSnapshot();
            annots.forEach(annot => ApryseSDK.annotEndStyle(annot, newLineEnding));

            ApryseSDK.handleAnnotationChanged(annots, 'modify', 'lineEnd', snapshotBefore);
            ReduxUpdater.annotationSidepanelRefresh();
            ReduxUpdater.ribbonRefresh();
        };

        const initialValue = allEqual ? firstValue : undefined;

        return renderLineEnding(initialValue, showEditMode, setNewLineEnding);
    };

    const renderMultiSelectionBorderStyle = (annots: Core.Annotations.Annotation[]) => {
        // Same functionality as for lineStyle,
        // but with different displayTitle!
        const displayTitle = i18next.t('annotPropsPanel.appearanceProps.borderStyle');

        return renderMultiSelectionLineStyle(annots, displayTitle);
    };

    const renderMultiSelectionLineStyle = (
        annots: Core.Annotations.Annotation[],
        overrideTitle?: string,
    ) => {
        const showEditMode = isAnnotationEditMode() && canEditAnyAnnot(annots);

        let allEqual: boolean;
        const firstValue = BorderInfoToStyleString(annots[0] as Core.Annotations.MarkupAnnotation);

        if (annots.length === 1) {
            allEqual = true;
        } else {
            allEqual = annots
                .slice(1)
                .every(
                    annot =>
                        BorderInfoToStyleString(annot as Core.Annotations.MarkupAnnotation) ===
                        firstValue,
                );
        }

        const setNewStyle = (newStyle: string) => {
            const snapshotBefore = CustomHistoryManager.takeAnnotationsSnapshot();
            annots.forEach(annot => ApryseSDK.annotStyle(annot, newStyle));

            ApryseSDK.handleAnnotationChanged(annots, 'modify', 'lineStyle', snapshotBefore);
            ReduxUpdater.annotationSidepanelRefresh();
            ReduxUpdater.ribbonRefresh();
        };

        const displayTitle: string =
            overrideTitle ?? i18next.t('annotPropsPanel.appearanceProps.lineStyle');
        const initialValue = allEqual ? firstValue : undefined;
        return renderLineStyle(displayTitle, initialValue, showEditMode, setNewStyle);
    };

    const renderMultiSelectionCloudyLineStyle = (
        annots: Core.Annotations.Annotation[],
        overrideTitle?: string,
    ) => {
        const showEditMode = isAnnotationEditMode() && canEditAnyAnnot(annots);

        let allEqual: boolean;
        const firstValue = BorderInfoToStyleString(annots[0] as Core.Annotations.MarkupAnnotation);

        if (annots.length === 1) {
            allEqual = true;
        } else {
            allEqual = annots
                .slice(1)
                .every(
                    annot =>
                        BorderInfoToStyleString(annot as Core.Annotations.MarkupAnnotation) ===
                        firstValue,
                );
        }

        const setNewCloudyStyle = (newCloudyStyle: string) => {
            const snapshotBefore = CustomHistoryManager.takeAnnotationsSnapshot();
            annots.forEach(annot => ApryseSDK.annotStyle(annot, newCloudyStyle));

            ApryseSDK.handleAnnotationChanged(annots, 'modify', 'lineCloudyStyle', snapshotBefore);
            ReduxUpdater.annotationSidepanelRefresh();
            ReduxUpdater.ribbonRefresh();
        };

        const displayTitle: string =
            overrideTitle ?? i18next.t('annotPropsPanel.appearanceProps.lineStyle');
        const initialValue = allEqual ? firstValue : undefined;
        return renderCloudyLineStyle(displayTitle, initialValue, showEditMode, setNewCloudyStyle);
    };

    const renderMultiSelectionColor = (annots: Core.Annotations.Annotation[]) => {
        // Same functionality as for lineColor,
        // but with different displayTitle!
        const displayTitle = i18next.t('annotPropsPanel.appearanceProps.color');

        return renderMultiSelectionLineColor(annots, displayTitle);
    };

    const renderMultiSelectionBorderColor = (annots: Core.Annotations.Annotation[]) => {
        // Same functionality as for lineColor,
        // but with different displayTitle!
        const displayTitle = i18next.t('annotPropsPanel.appearanceProps.borderColor');

        return renderMultiSelectionLineColor(annots, displayTitle);
    };

    const renderMultiSelectionLineColor = (
        annots: Core.Annotations.Annotation[],
        overrideTitle?: string,
    ) => {
        const showEditMode = isAnnotationEditMode() && canEditAnyAnnot(annots);
        let allEqual: boolean;
        const firstValue = annots[0].Color;

        if (annots.length === 1) {
            allEqual = true;
        } else {
            allEqual = annots.slice(1).every(annot => annot.Color === firstValue);
        }

        const setNewColor = (newColor: number) => {
            const snapshotBefore = CustomHistoryManager.takeAnnotationsSnapshot();
            annots.forEach(annot => ApryseSDK.annotColor(annot, newColor));

            ApryseSDK.handleAnnotationChanged(annots, 'modify', 'color', snapshotBefore);
            ReduxUpdater.annotationSidepanelRefresh();
            ReduxUpdater.ribbonRefresh();
        };

        const initialValue = allEqual ? intColor(firstValue) : null;
        const displayTitle: string =
            overrideTitle ?? i18next.t('annotPropsPanel.appearanceProps.lineColor');

        return renderColorProperty(
            displayTitle,
            initialValue,
            showEditMode,
            ReduxUpdater.lists.colors ?? [],
            mdiPalette,
            setNewColor,
        );
    };

    const renderMultiSelectionBorderWidth = (annots: Core.Annotations.Annotation[]) => {
        // Same functionality as for lineWidth,
        // but with different displayTitle!
        const displayTitle = i18next.t('annotPropsPanel.appearanceProps.borderWidth');

        return renderMultiSelectionLineWidth(annots, displayTitle);
    };

    const renderMultiSelectionLineWidth = (
        annots: Core.Annotations.Annotation[],
        overrideTitle?: string,
    ) => {
        const showEditMode = isAnnotationEditMode() && canEditAnyAnnot(annots);

        let allEqual: boolean;
        const firstValue = ApryseSDK.annotThickness(annots[0]);

        if (annots.length === 1) {
            allEqual = true;
        } else {
            allEqual = annots
                .slice(1)
                .every(annot => ApryseSDK.annotThickness(annot) === firstValue);
        }

        const setNewWidth = (newWidth: number) => {
            const snapshotBefore = CustomHistoryManager.takeAnnotationsSnapshot();
            annots.forEach(annot => ApryseSDK.annotThickness(annot, newWidth));

            ApryseSDK.handleAnnotationChanged(annots, 'modify', 'lineWidth', snapshotBefore);
            ReduxUpdater.annotationSidepanelRefresh();
            ReduxUpdater.ribbonRefresh();
        };

        const initialValue = allEqual ? firstValue : 0;
        return renderLineWidth(initialValue, showEditMode, setNewWidth, overrideTitle);
    };

    const renderMultiSelectionFillColor = (annots: Core.Annotations.Annotation[]) => {
        const showEditMode = isAnnotationEditMode() && canEditAnyAnnot(annots);
        const firstValue = removeTransparency(ApryseSDK.annotFillColor(annots[0]));

        let allEqual: boolean;

        if (annots.length === 1) {
            allEqual = true;
        } else {
            allEqual = annots
                .slice(1)
                .every(annot => removeTransparency(ApryseSDK.annotFillColor(annot)) === firstValue);
        }

        const setNewValue = (newColor: number) => {
            const snapshotBefore = CustomHistoryManager.takeAnnotationsSnapshot();
            annots.forEach(annot => ApryseSDK.annotFillColor(annot, newColor));

            ApryseSDK.handleAnnotationChanged(annots, 'modify', 'fillColor', snapshotBefore);
            ReduxUpdater.annotationSidepanelRefresh();
            ReduxUpdater.ribbonRefresh();
        };

        const displayTitle = i18next.t('annotPropsPanel.specificProps.fillColor');
        const initialValue = allEqual ? firstValue : null;

        return renderColorProperty(
            displayTitle,
            initialValue,
            showEditMode,
            ReduxUpdater.lists.fillColors ?? [],
            mdiFormatColorFill,
            setNewValue,
        );
    };

    const renderInfoProps = (annot: Core.Annotations.Annotation) => {
        const modified = annot.DateModified;
        return (
            <>
                <h3
                    style={{
                        margin: '0px',
                    }}
                >
                    {i18next.t('annotPropsPanel.general')}
                </h3>
                <br />
                {renderStringProperty('author', annot.Author)}
                {renderStringProperty(
                    'creationDate',
                    ApryseSDK.getCreationTime(annot).toLocaleString('de-DE', dateTimeFormat),
                )}
                {renderStringProperty(
                    'modDate',
                    modified === null ? '' : modified.toLocaleString('de-DE', dateTimeFormat),
                )}
                {renderStringProperty('subject', annot.Subject)}
            </>
        );
    };

    const renderColorProperty = (
        displayTitle: string,
        intColor: number | null,
        editMode: boolean,
        colors: IColorRGB[],
        icon: string,
        setNewColorFn: (newColor: number) => void,
    ) => {
        const colorOptions: IDropdownOption[] = colorsToDropdownOptions(colors);
        const isConfigured: boolean = isButtonPresent('colorOptions');
        const showEditMode: boolean =
            isConfigured && colorOptions && colorOptions.length > 1 && editMode;

        return (
            <TextField
                className={styles.dropDown}
                fullWidth
                select
                disabled={!showEditMode}
                label={displayTitle}
                value={intColor}
                onChange={event => setNewColorFn(parseInt(event.target.value as string))}
                margin="dense"
                size="small"
                sx={darkTextFieldSx(!showEditMode)}
            >
                {(!showEditMode
                    ? [{ key: intColor, text: toCSSColor(intColor).toUpperCase() }]
                    : colorOptions
                ).map(color => (
                    <MenuItem className={styles.dropDown} key={color.key} value={color.key}>
                        <Icon path={icon} style={{ color: toCSSColor(color.key as number) }} />
                        {color.text}
                    </MenuItem>
                ))}
            </TextField>
        );
    };

    const renderLineStart = (
        lineStart: Core.Annotations.LineEndType | undefined,
        editMode: boolean,
        setNewLineStart: (newValue: Core.Annotations.LineEndType) => void,
    ) => {
        const lineStarts: ILineEndings[] = ReduxUpdater.lists.lineStarts ?? [];
        const dropdownOptions: IDropdownOption[] = lineEndingsToDropdownOptions(lineStarts);
        const isConfigured: boolean = isButtonPresent('lineStartOptions');
        const showEditMode: boolean =
            isConfigured && dropdownOptions && dropdownOptions.length > 1 && editMode;

        const lineStartName: string | undefined =
            lineStart === undefined ? undefined : ApryseSDK.lineEndingStyleToString(lineStart);
        const initialIndex: number | undefined =
            lineStartName === undefined
                ? undefined
                : dropdownOptions.findIndex(entry => entry.key === lineStartName);

        const defaultSelectedKey =
            initialIndex === undefined ? null : dropdownOptions[initialIndex].key;
        return (
            <TextField
                className={styles.dropDown}
                fullWidth
                select
                disabled={!showEditMode}
                label={i18next.t('annotPropsPanel.appearanceProps.lineStart')}
                value={defaultSelectedKey}
                onChange={event =>
                    setNewLineStart(ApryseSDK.stringToLineEndingStyle(event.target.value))
                }
                margin="dense"
                size="small"
                sx={darkTextFieldSx(!showEditMode)}
            >
                {dropdownOptions.map(option => (
                    <MenuItem className={styles.dropDown} key={option.key} value={option.key}>
                        {lineEndingStartIcon[option.data]}
                        {option.text}
                    </MenuItem>
                ))}
            </TextField>
        );
    };

    const renderLineEnding = (
        lineEnding: Core.Annotations.LineEndType | undefined,
        editMode: boolean,
        setNewLineEnding: (newValue: Core.Annotations.LineEndType) => void,
    ) => {
        const lineEndings: ILineEndings[] = ReduxUpdater.lists.lineEnds ?? [];
        const dropdownOptions: IDropdownOption[] = lineEndingsToDropdownOptions(lineEndings);
        const isConfigured: boolean = isButtonPresent('lineEndOptions');
        const showEditMode: boolean =
            isConfigured && dropdownOptions && dropdownOptions.length > 1 && editMode;

        const lineEndingName: string | undefined =
            lineEnding === undefined ? undefined : ApryseSDK.lineEndingStyleToString(lineEnding);
        const initialIndex: number | undefined =
            lineEndingName === undefined
                ? undefined
                : dropdownOptions.findIndex(entry => entry.key === lineEndingName);

        const defaultSelectedKey =
            initialIndex === undefined ? null : dropdownOptions[initialIndex].key;
        return (
            <TextField
                className={styles.dropDown}
                fullWidth
                select
                disabled={!showEditMode}
                label={i18next.t('annotPropsPanel.appearanceProps.lineEnd')}
                value={defaultSelectedKey}
                onChange={event =>
                    setNewLineEnding(ApryseSDK.stringToLineEndingStyle(event.target.value))
                }
                margin="dense"
                size="small"
                sx={darkTextFieldSx(!showEditMode)}
            >
                {dropdownOptions.map(option => (
                    <MenuItem className={styles.dropDown} key={option.key} value={option.key}>
                        {lineEndingEndIcon[option.data]}
                        {option.text}
                    </MenuItem>
                ))}
            </TextField>
        );
    };

    const renderLineWidth = (
        width: number | undefined,
        editMode: boolean,
        setNewWidth: (newValue: number) => void,
        overrideTitle?: string,
    ) => {
        const lineWidths: ILineWidth[] = ReduxUpdater.lists.lineWidths ?? [];
        const dropdownOptions: IDropdownOption[] = lineWidthsToDropdownOptions(lineWidths);
        const isConfigured: boolean = isButtonPresent('lineWidthOptions');
        const showEditMode: boolean =
            isConfigured && dropdownOptions && dropdownOptions.length > 1 && editMode;
        const displayTitle =
            overrideTitle ?? i18next.t('annotPropsPanel.appearanceProps.lineWidth');

        return (
            <TextField
                className={styles.dropDown}
                fullWidth
                select
                disabled={!showEditMode}
                label={displayTitle}
                value={width}
                onChange={event => setNewWidth(parseFloat(event.target.value as string))}
                margin="dense"
                size="small"
                sx={darkTextFieldSx(!showEditMode)}
            >
                {dropdownOptions.map(option => (
                    <MenuItem className={styles.dropDown} key={option.key} value={option.key}>
                        {lineWidthIcon[`lineWidth${option.key}`]}
                        {option.text}
                    </MenuItem>
                ))}
            </TextField>
        );
    };

    const renderLineStyle = (
        displayTitle: string,
        styleName: string | undefined,
        editMode: boolean,
        setNewLineStyle: (newValue: string) => void,
    ) => {
        const borderStyles = ReduxUpdater.lists.lineStyles ?? [];
        const dropdownOptions = borderStylesToDropdownOptions(borderStyles);
        const isConfigured: boolean = isButtonPresent('lineStyleOptions');
        const showEditMode: boolean =
            isConfigured && dropdownOptions && dropdownOptions.length > 1 && editMode;

        let initialStyle: ILineStyle | undefined = undefined;
        if (styleName) {
            initialStyle = borderStyles.find(
                borderStyle =>
                    borderStyle.name === styleName || borderStyle.name.startsWith(styleName),
            );
        }

        return (
            <TextField
                id={GlobalVariables.isForTestRun ? 'annotPropsSidebarLineStyle' : undefined}
                className={styles.dropDown}
                fullWidth
                select
                disabled={!showEditMode}
                label={displayTitle}
                value={initialStyle?.name}
                onChange={event => setNewLineStyle(event.target.value as string)}
                margin="dense"
                size="small"
                sx={darkTextFieldSx(!showEditMode)}
            >
                {dropdownOptions.map(option => (
                    <MenuItem className={styles.dropDown} key={option.key} value={option.key}>
                        {lineStyleIcon[option.data]}
                        {translateLineStyle(option.text)}
                    </MenuItem>
                ))}
            </TextField>
        );
    };

    const renderCloudyLineStyle = (
        displayTitle: string,
        styleName: string | undefined,
        editMode: boolean,
        setNewCloudyLineStyle: (newValue: string) => void,
    ) => {
        const borderStyles = ReduxUpdater.lists.lineStylesCloudy ?? [];
        const dropdownOptions = borderStylesToDropdownOptions(borderStyles);
        const isConfigured: boolean = isButtonPresent('lineStyleCloudyOptions');
        const showEditMode: boolean =
            isConfigured && dropdownOptions && dropdownOptions.length > 1 && editMode;

        let initialStyle: ILineStyle | undefined = undefined;
        if (styleName) {
            initialStyle = borderStyles.find(
                borderStyle =>
                    borderStyle.name === styleName || borderStyle.name.startsWith(styleName),
            );
        }

        return (
            <TextField
                id={GlobalVariables.isForTestRun ? 'annotPropsSidebarLineStyle' : undefined}
                className={styles.dropDown}
                fullWidth
                select
                disabled={!showEditMode}
                label={displayTitle}
                value={initialStyle?.name}
                onChange={event => setNewCloudyLineStyle(event.target.value as string)}
                margin="dense"
                size="small"
                sx={darkTextFieldSx(!showEditMode)}
            >
                {dropdownOptions.map(option => (
                    <MenuItem className={styles.dropDown} key={option.key} value={option.key}>
                        {lineStyleIcon[option.data]}
                        {translateLineStyle(option.text)}
                    </MenuItem>
                ))}
            </TextField>
        );
    };

    const renderStringProperty = (key: string, value: string) => {
        const title = i18next.t(`annotPropsPanel.generalProps.${key}`);
        return (
            <TextField
                id={
                    GlobalVariables.isForTestRun
                        ? `annotPropsSidebarStrProp${upperCaseBegin(key)}`
                        : undefined
                }
                className={styles.fullWidth}
                key={`prop-${genRandomKey()}`}
                label={title}
                value={value}
                disabled={true}
                margin="dense"
                size="small"
                sx={darkTextFieldSx(true)}
                inputProps={{
                    style: {
                        WebkitTextFillColor: isDark ? 'rgba(224,224,224,0.35)' : 'black',
                    },
                }}
            />
        );
    };

    const layout = React.useMemo(() => {
        return (
            <div className={concatClassNames(styles.annotPropsTable)}>
                {selectedAnnots.length === 1 ? renderInfoProps(selectedAnnots[0]) : null}
                {selectedAnnots.length > 0 ? renderStylingProps(selectedAnnots) : null}
            </div>
        );
    }, [forceRefresh, selectedAnnotationsIds]);

    return layout;
};
